import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "className", "delay", "fwdRef", "hidden", "id", "role", "style", "tagName"];
import React, { forwardRef, useEffect, useState } from 'react';
import { buildVersionedClassNames } from '@op/opds.opux-utilities';
/** Component for notifying screen readers about dynamic status updates.
 * Mainly intended to be used internally by OPUX components.
 *
 * [`role="status"`](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/status_role) should
 * be used for information that is not important enough to justify an alert, such as the results of a search.
 * The announcement is read once screen reader has finished reading current focus without interrupting the user.
 *
 * The [`alert`](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/alert_role) role should
 * be used for important, and usually time-sensitive, information that requires immediate attention, such
 * as an error message. The announcement is read immediately and interrupts the current focus.
 *
 * Additional props (including `ref`) will be forwarded to the wrapping HTML element.
 */

export const StatusMessage = _ref => {
  let {
      children,
      className,
      delay = 100,
      fwdRef,
      hidden = false,
      id,
      role = 'status',
      style,
      tagName: Tag = 'span'
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);
  return (
    /*#__PURE__*/
    // @ts-expect-error -- Typing fwdRef as HTMLElement will not work with any tag therefore we just ignore the error
    React.createElement(Tag
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- Polymorphic "tagName" / "Tag" makes Typescript crash without this
    // @ts-ignore
    , _extends({
      className: buildVersionedClassNames('ds-status-message', className, {
        'ds-status-message--visually-hidden': hidden
      }),
      ref: fwdRef,
      role: role,
      style: _extends({
        transitionDelay: delay + "ms"
      }, style),
      id: id
    }, props), visible && children)
  );
};
const Fwd = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/React.createElement(StatusMessage, _extends({}, props, {
  fwdRef: ref
})));
Fwd.displayName = 'StatusMessage';
export default Fwd;